import React, { Fragment, useState, useRef, useEffect } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import { classNames } from "../../../helpers/classNames";
import Image from "../../elements/Image";
import NoData from "../../../assets/images/no-results-bg.2d2c6ee3.png";
import Search from "./Search";

const placements = {
  bottom: "bottom",
  "bottom-left": "bottom-start",
  "bottom-right": "bottom-end",
  top: "top",
  "top-left": "top-start",
  "top-right": "top-end",
  right: "right",
  left: "left",
  "": "bottom",
};

const getFallbackPlacements = (xPlacement) => {
  const fallbacks = {
    bottom: ["bottom-start", "bottom-end", "top", "top-start", "top-end"],
    "bottom-left": ["bottom-start", "bottom", "bottom-end", "top-start", "top"],
    "bottom-right": ["bottom-end", "bottom", "bottom-start", "top-end", "top"],
    top: ["top-start", "top-end", "bottom", "bottom-start", "bottom-end"],
    "top-left": ["top-start", "top", "top-end", "bottom-start", "bottom"],
    "top-right": ["top-end", "top", "top-start", "bottom-end", "bottom"],
    right: ["right", "left", "bottom-end", "top-end"],
    left: ["left", "right", "bottom-start", "top-start"],
    "": ["bottom-start", "bottom-end", "top", "top-start", "top-end"],
  };

  return fallbacks[xPlacement] || fallbacks.bottom;
};

const Select = ({ dropdownData = [], selectedValue, onChange = () => { }, placeholder, isSearch = false, xPlacement = "bottom-left", flip = true, dropdownClass, dropdownButtonClass, optionsClass, onSearchHandler = () => { }, onScroll = () => { }, loading, label, labelClasses, errorType, errorText, required, isDisabled = false, checked = true, offset = 8, strategy = 'absolute', note, noteClasses, ...props }) => {
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const dropdownRef = useRef(null);

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: placements[xPlacement],
    strategy: strategy,
    modifiers: [
      { name: "offset", options: { offset: [0, offset] } },
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "computeStyles", options: { gpuAcceleration: false } },
      flip
        ? {
          name: "flip",
          options: {
            fallbackPlacements: getFallbackPlacements(xPlacement),
            flipVariations: true,
          },
        }
        : {
          name: "flip",
          enabled: false,
        },
    ].filter(Boolean),
  });

  useEffect(() => {
    if (open && update) {
      update();
    }
  }, [open, update]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    setList(dropdownData);
  }, [JSON.stringify(dropdownData)]);

  useEffect(() => {
    if ((selectedValue || selectedValue !== undefined) && dropdownData && Array.isArray(dropdownData) && dropdownData?.length > 0) {
      setSelected(dropdownData.filter((item) => item?.value === selectedValue || item?._id === selectedValue)?.[0] || null);
    } else {
      setSelected(null);
    }
  }, [JSON.stringify(selectedValue), JSON.stringify(dropdownData)]);

  return (
    <div className="relative w-full">
      {label && (
        <div className={classNames("text-xs font-medium text-shades-01 mb-1", labelClasses)}>
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <Listbox
        as={"div"}
        className="relative"
        value={selected}
        onChange={(val) => {
          setSelected(val);
          onChange(val?.value, val);
        }}
        disabled={isDisabled}
      >
        {({ open }) => (
          <>
            <ListboxButton ref={setReferenceElement} className="flex items-center w-full text-left">
              <div className={classNames("relative w-full h-12 flex items-center gap-2 pr-6 pl-3 rounded-md bg-transparent border text-sm text-black !ring-0 !outline-0 focus:border-shades-01 transition-all duration-200 ", dropdownButtonClass, open ? "border-shades-01" : "border-shades-02")}>
                {selected?.image && (
                  <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                    <Image src={selected?.image} alt={selected?.image} quality={0.2} className={"w-full h-full object-cover object-center"} />
                  </div>
                )}
                {selected?.icon && (
                  <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                    <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                      <i className={classNames("fa-fw", selected?.icon)}></i>
                    </div>
                  </div>
                )}
                 {selected?.flag && selected?.flag}
                <div className="whitespace-nowrap flex-grow truncate">
                  {selected?.name || <span className="text-black">{placeholder}</span>}
                </div>
                <div className="text-xs text-black ml-auto absolute top-1/2 right-2 -translate-y-1/2">{loading ? <i className={"fa-duotone fa-spinner-third animate-spin text-sm"} /> : <i className={"fa-light fa-fw fa-chevron-down"}></i>}</div>
              </div>
            </ListboxButton>
            <Transition
              as={Fragment}
              className="z-20"
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              beforeEnter={() => setPopperElement(setPopperElement.current)}
              afterLeave={() => {
                setPopperElement(null);
              }}
              ref={setPopperElement} style={styles.popper} {...attributes.popper}
            >
              <ListboxOptions
                modal={false}
                //anchor="bottom"
                //transition
                className={classNames("mt-1 w-72 origin-top-right bg-shades-07 divide-y divide-shades-03 rounded-md shadow-lg border border-shades-02 ring-0 !overflow-hidden", dropdownClass)}
              >
                {isSearch && (
                  <div className="py-2 px-3">
                    <Search placeholder={"Search"} iconClasses={""} search={(e) => onSearchHandler(e)} />
                  </div>
                )}
                {Array.isArray(list) && !loading && list?.length > 0 ? (
                  <div className="overflow-auto mb-1 w-full py-1 flex flex-col max-h-60 scrollbar divide-y divide-shades-03" onScroll={onScroll}>
                    {list.map((item, index) => (
                      <ListboxOption key={index} onMouseOver={(e) => { }} className={() => classNames("relative py-2 pl-8 pr-2 text-sm cursor-pointer transition-all duration-200", optionsClass, selected?.value === item?.value ? "text-shades-orange bg-shades-orange/20" : "text-shades-01 hover:bg-shades-03 hover:text-shades-06")} value={item}>
                        <>
                          <div className={classNames("truncate flex items-center gap-2", selected?.value === item?.value ? "font-medium" : " ")} style={typeof item?.style === "function" ? item?.style(item) : item?.style}>
                            {item?.icon && (
                              <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                                <i className={classNames("fa-fw", item?.icon)}></i>
                              </div>
                            )}
                            {item?.image && (
                              <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                                <Image src={item.image} alt={item.image} className={"w-full h-full object-cover object-center"} />
                              </div>
                            )}
                            <div className="flex flex-col gap-1">
                              {item?.name}
                              {item?.description && (<div className={classNames("text-xs whitespace-pre-wrap", selected?.value === item?.value ? "text-shades-orange" : "text-shades-01")}>{item?.description}</div>)}
                            </div>
                          </div>
                          {selected?.value === item?.value && checked && (
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2 text-base">
                              <i className="fa-regular fa-fw fa-check"></i>
                            </div>
                          )}
                        </>
                      </ListboxOption>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center w-full py-3 space-y-3">
                    <div className={classNames("relative overflow-hidden w-32 h-32")}>
                      <Image src={NoData} alt="no-data" className="w-full h-full" />
                    </div>
                  </div>
                )}
              </ListboxOptions>
            </Transition>
          </>
        )}
      </Listbox>
      {note && (
        <div className={classNames("text-xs mt-1 text-shades-01", noteClasses)}>{note}</div>
      )}
      {errorType && errorType === "error" && (
        <div className="text-xs text-red-600 mt-1">
          <i className="fa-regular fa-fw fa-square-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "warning" && (
        <div className="text-xs text-amber-600 mt-1">
          <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "success" && (
        <div className="text-xs text-green-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-check text-sm mr-1"></i>
          {errorText}
        </div>
      )}
      {errorType && errorType === "info" && (
        <div className="text-xs text-sky-600 mt-1">
          <i className="fa-regular fa-fw fa-circle-info text-sm mr-1"></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default Select;
